import React, { useCallback, useEffect, useState } from 'react';
import useForm from '@utils/useForm';
import { useHistory } from 'react-router-dom';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { updateBasicDetails, updateOccupation, updateOccupationCode } from '@redux/slices/SIO/step1Slice';
import { BasicDetailsForm } from '@models/SIO/BasicDetails.model';
import { getOccupations } from 'src/api';
import { updateSIOOccupations } from '@redux/slices/staticSlice';
import { get, find, isEmpty, isNil } from 'lodash';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Heading from '@components/Heading';
import InformationBox from '@components/InformationBox';
import RadioButtonGroup from '@components/RadioButtonGroup';
import RadioButton from '@components/RadioButton';
import Link from '@components/Link';
import Dropdown from '@components/Dropdown';
import CheckboxList from '@components/CheckboxList';
import Checkbox from '@components/Checkbox';
import MultiSteps from '@components/MultiSteps';
import Text from '@components/Text';
import Button from '@components/Button';
import ContactBox from '@components/ContactBox';
import DayOfBirth from './components/DayOfBirth';
import Bullet from '@components/Bullet';
import PlanInformation from './components/PlanInformation';
import Divider from '@components/Divider';
import { validate } from './service';
const page = 'Basic Details';

const trackOnload = (fromData: BasicDetailsForm, errorMessage: string = ""): void => {
  const { month, day, year } = fromData.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const gender = Helpers.getGenderNameByCode(fromData.gender || '');
  const GA = GAAviva.getInstance();
  let { user, form, page } = GA.digitalData;
  user = { ...user, person: { age: `${age}`}}
  form = { ...form,
    name: 'Basic Details',
    field: { ...form?.field, Gender: gender}
  };
  GA.trackPageLoad({ user, form, page });
}
const trackFormSubmmit = (): void =>  {
  const GA = GAAviva.getInstance();
  GA.trackFormSuccess({});
}

// Should be exported to seperate files;
export interface BasicDetailsProps { }

function BasicDetails() {
  console.log(`Aviva - ${page}`);
  const history = useHistory();
  const dispatch = useAvivaDispatch();
  const brochureUrl = useAvivaSelector(state => state.staticData.brochureUrl);

  const initValue = useAvivaSelector(state => state.sioStep1.basicDetails);
  const occupations = useAvivaSelector(state => state.staticData.sioOccupations);
  const nationlaities = useAvivaSelector(state => state.staticData.nationlaities);
  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const [genericError, setGenericError] = useState<string | undefined>(undefined);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);

  const urlParams: URLSearchParams = Helpers.getURLSearchParams();

  const { handleChange, values, errors, formValid } = useForm(new BasicDetailsForm(initValue), () => { }, validate);

  const onContinue = () => {
    const selectedOccupation = find(occupations, { desc: values['occupationName'] });
    console.debug({ selectedOccupation, values });
    dispatch(updateBasicDetails(values));
    dispatch(updateOccupation(selectedOccupation || { code: '', desc: '' }));
    dispatch(updateOccupationCode(selectedOccupation?.code || ''));
    trackOnload(values);
    trackFormSubmmit();
    history.push('/sio/coverage')
  }

  const gotoContact = () => {
    history.push('/sio/contact')
  }

  const getGenericError = useCallback(() => {
    const dobError = urlParams.get('doberror');
    const genderError = urlParams.get('gendererror');
    const nationalityError = urlParams.get('nationalityerror');

    let message = '';
    if (dobError === 'true') {
      message = 'Please ensure date of birth matches with Myinfo data.';
    }
    if (genderError === 'true') {
      message = 'Please ensure gender matches with Myinfo data.';
    }
    if (nationalityError === 'true') {
      message = 'Please ensure nationality matches with Myinfo data.';
    }
    setGenericError(message);
    return message;
  }, [urlParams])

  const getOccupationsListing = useCallback(async () => {
    try {
      const response = await getOccupations('sio');
      const list = get(response, 'data.occupations', []);
      dispatch(updateSIOOccupations(list))
    } catch (error) {
      console.error('getOccupationsListing', error);
      setGenericError("Couldn't get Occupations");
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isPageLoading) {
      const errorMessage = getGenericError();
      if (!occupations) {
        getOccupationsListing();
      }
      trackOnload(values, errorMessage)
      setIsPageLoading(true);
    }

  }, [occupations, getGenericError, getOccupationsListing, isPageLoading, values]);


  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Let's get a quick quote..." />
          <Heading margin="0 0 30px"> Let&apos;s get a quick quote... </Heading>
        </FullGrid>
        <LeftGrid>
          {!isEmpty(genericError) && !isNil(genericError) && (
            <InformationBox variant="error">
              <Text>{genericError}</Text>
            </InformationBox>
          )}
          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <Heading variant="label"> Date of birth </Heading>
            {!errors?.dob?.isValid && errors?.dob?.message && (
              <Bullet variant="error">{errors?.dob?.message}</Bullet>
            )}
            <DayOfBirth value={values['dob']} name="dob" onChange={handleChange} error={!errors?.dob?.isValid && errors?.dob?.message} />
            <Heading variant="label"> Gender </Heading>
            <RadioButtonGroup value={values['gender']} name="gender" onChange={handleChange}>
              <RadioButton text="Male" value='M' />
              <RadioButton text="Female" value='F' />
            </RadioButtonGroup>

            <Heading variant="label"> Are you a smoker? </Heading>
            <RadioButtonGroup value={values['smoker']} name="smoker" onChange={handleChange}>
              <RadioButton text="Yes" value='Y' />
              <RadioButton text="No" value='N' />
            </RadioButtonGroup>

            <Heading variant="label"> Occupation </Heading>
            <Dropdown variant="auto-complete" value={values['occupationName']} name="occupationName" onChange={handleChange} source={occupations || []} bindLabel='desc' bindId='desc' />

            <Heading variant="label"> Nationality </Heading>
            <Dropdown variant="auto-complete" value={values['nationalityCode']} name="nationalityCode" onChange={handleChange} source={nationlaities} bindId='code' bindLabel='description' />

            <InformationBox hasClose>
              <Text> Don’t see your nationality? Please leave your contact details <Link text="here" variant="button" onClick={gotoContact} /> and our financial adviser representative will be in touch with you shortly. </Text>
            </InformationBox>

            <Heading variant="sub" margin="0 0 15px"> Please tick and confirm </Heading>
            <Divider margin="0 0 32px" />
            <CheckboxList>
              <Checkbox margin="0 0 30px" name="required1" checked={values['required1']} onChange={handleChange}>
                <Text> I am a Singapore Resident with a valid NRIC or FIN*.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required2" checked={values['required2']} onChange={handleChange}>
                <Text> I have resided in Singapore for more than 183 days in the last 12 months.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required3" checked={values['required3']} onChange={handleChange}>
                <Text> I am not an undischarged bankrupt.</Text>
              </Checkbox>
              <Checkbox margin="0 0 30px" name="required4" checked={values['required4']} onChange={handleChange}>
                <Text> I am not purchasing this policy to replace any existing policy with Singapore Life Ltd. (“Singlife”) or other insurer(s). </Text>
              </Checkbox>
            </CheckboxList>
            <Heading variant='desc'>
              *Employment Pass / S Pass / Dependant Pass / Student Pass / Long Term Visit Pass / Work Permit (exclude foreign domestic helper or construction worker)
            </Heading>
            <Heading variant="desc">
              <b>Please note:</b> Singlife Simple Term is available via Singlife&apos;s appointed distributors and intermediaries as well. Product features offered and premiums are the same irrespective of the purchase channel, i.e., buy online here or via appointed financial adviser representatives. If you wish to buy online without financial advice, please proceed. Alternatively, you may visit our website to leave your contact details and call back request, and we will assign a financial adviser representative to you.
            </Heading>

          </form>

          <Button margin="40px 0 40px" disabled={!formValid} label="Continue" onClick={onContinue}></Button>
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <PlanInformation brochureUrl={brochureUrl} />
          <ContactBox items={contactInformation} />
        </RightGrid>
      </ContainerGrid>

    </Container>
  );
};

export default BasicDetails;
